import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { logout } from '../../redux/slices/authSlice';

const AdminSidebar = ({ isOpen, onToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [aiInsights, setAiInsights] = useState({});
  const [quickStats, setQuickStats] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    setAiInsights({
      dashboard: {  type: 'urgent', color: 'red' },
      users: {  type: 'new', color: 'green' },
      properties: {  type: 'updates', color: 'yellow' },
      agents: {type: 'pending', color: 'blue' },
      blogs: {  type: 'draft', color: 'purple' },
      contacts: { type: 'unread', color: 'orange' }
    });
    
    setQuickStats({
      totalProperties: 147,
      activeAgents: 23,
      pendingContacts: 15,
      monthlyRevenue: 89500,
      conversionRate: 16.3,
      aiEfficiency: 94
    });

    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Close sidebar function - centralized
  const handleCloseSidebar = () => {
    if (isOpen) {
      onToggle();
    }
  };

  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      key: 'dashboard',
      gradient: 'from-blue-500 to-indigo-600'
    },

    {
      path: '/admin/properties',
      label: 'Properties',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      key: 'properties',
      gradient: 'from-purple-500 to-pink-600'
    },
  
    {
      path: '/admin/blogs',
      label: 'AI Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      key: 'blogs',
      gradient: 'from-teal-500 to-cyan-600'
    },
    {
      path: '/admin/contacts',
      label: 'Contacts',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      key: 'contacts',
      gradient: 'from-violet-500 to-purple-600'
    },
    {
  path: '/admin/testimonials',
  label: 'Testimonials',
  icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  ),
  key: 'testimonials',
  gradient: 'from-amber-500 to-orange-600'
},

  ];

  const getBadgeColor = (color) => {
    const colors = {
      red: 'bg-red-500 animate-pulse',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500 animate-bounce',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  const renderBadge = (key) => {
    const insight = aiInsights[key];
    if (!insight || !insight.count) return null;

    return (
      <motion.span
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        className={`${getBadgeColor(insight.color)} text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg ml-auto`}
      >
        {insight.count}
      </motion.span>
    );
  };

  const sidebarVariants = {
    open: {
      width: 320,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    closed: {
      width: 80,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    }
  };

  return (
    <>
      {/* Mobile Backdrop - Click to close */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={handleCloseSidebar}
          />
        )}
      </AnimatePresence>
      
      {/* Sidebar */}
      <motion.div 
        variants={sidebarVariants}
        animate={isOpen ? "open" : "closed"}
        className="fixed left-0 top-0 h-full bg-gradient-to-b from-white via-slate-50 to-gray-100 border-r border-gray-200 z-50 shadow-2xl overflow-hidden"
      >
        
        {/* Header with Close Button */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 relative">
          <motion.div 
            className="flex items-center gap-3"
            animate={isOpen ? { justifyContent: "space-between" } : { justifyContent: "center" }}
          >
            <div className="flex items-center gap-3">
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 10 }}
                className="w-12 h-12 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg relative"
              >
                <span>🧠</span>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              </motion.div>
              
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    THINKREALTY
                    </h1>
                   
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Close Button (X) - Only visible when open */}
            <AnimatePresence>
              {isOpen && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8, rotate: 90 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.8, rotate: 90 }}
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 90,
                    backgroundColor: '#fee2e2'
                  }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleCloseSidebar}
                  className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 transition-all duration-200 group"
                  title="Close Sidebar"
                >
                  <svg className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

       

        {/* Navigation */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path || (item.path === '/admin' && location.pathname === '/admin');
            
            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <NavLink
                  to={item.path}
                  onClick={() => {
                    // Auto-close sidebar on mobile when navigating
                    if (window.innerWidth < 1024) {
                      handleCloseSidebar();
                    }
                  }}
                  className={`flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group relative ${
                    isActive
                      ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg`
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <div className="flex-shrink-0">
                    <motion.div whileHover={{ scale: 1.2 }}>
                      {item.icon}
                    </motion.div>
                  </div>
                  
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="flex-1 flex items-center justify-between"
                      >
                        <span className="font-bold">{item.label}</span>
                        {renderBadge(item.key)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {!isOpen && renderBadge(item.key) && (
                    <div className="absolute -top-1 -right-1">
                      {renderBadge(item.key)}
                    </div>
                  )}
                </NavLink>
              </motion.div>
            );
          })}
        </nav>

        {/* Footer with Logout and Collapse Button */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          {/* Collapse/Expand Toggle Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onToggle}
            className="flex items-center justify-center gap-3 w-full px-4 py-3 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-all font-bold"
          >
   <motion.svg
  animate={{ rotate: isOpen ? 180 : 0 }}
  transition={{ duration: 0.3 }}
  className="w-5 h-5"
  fill="none"
  stroke="currentColor"
  viewBox="0 0 24 24"
>
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    d="M14 5l7 7-7 7M10 5l-7 7 7 7"
  />
</motion.svg>

            <AnimatePresence>
              {isOpen && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Collapse
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Logout Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            className="flex items-center justify-center gap-4 w-full px-4 py-4 text-red-600 hover:bg-red-50 rounded-2xl transition-all font-bold"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <AnimatePresence>
              {isOpen && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* ESC Key Handler */}
        <div
          className="sr-only"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && isOpen) {
              handleCloseSidebar();
            }
          }}
        />
      </motion.div>
    </>
  );
};

export default AdminSidebar;
